import { Injectable, EventEmitter } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { findPhoneNumbersInText } from 'libphonenumber-js';
import { ApiService } from './api.service';
import { Contacts } from '@capacitor-community/contacts';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../environments/environment';
import { CustomAppBgService } from './custom-app-background.service';
import { ToastController } from '@ionic/angular';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  public conXDefaults: any = {};
  public appOnOff: boolean = true;
  public all_favorite_contacts: any = [];
  public active_q_calls: any = [];
  public activeq_call_id: any = '';
  public profiles: any = [];
  public speed_dial_list: any = {};
  public bypass_list: any = {
    contacts: [],
    groups: {},
  };
  public groups_list: any = {};
  public all_contacts: any = [];
  public riptec_contacts: any = [];
  public selectedContact: any = null;
  public contacts_selected_to_add_in_favourites: any = [];
  simpleSortedContacts = new EventEmitter<any>();

  constructor(
    private apiService: ApiService,
    private customAppBgService: CustomAppBgService,
    private toastController: ToastController,
    private router: Router,
  ) {}

  setSortedContactsForFavouritesAdd(contacts: any) {
    this.simpleSortedContacts.emit(contacts);
  }

  async handleUpdateAppDefaults(data: any) {
    await Preferences.set({
      key: 'conxhub-defaults',
      value: JSON.stringify(data),
    });
  }

  async initilizeAppDefaults() {
    let defaults: any = await Preferences.get({ key: 'conxhub-defaults' });
    if (defaults.value) {
      this.conXDefaults = JSON.parse(defaults.value);
    }
  }

  async initRiptecContacts() {
    return new Promise(async (resolve, reject) => {
      let imsi_number: any = await Preferences.get({
        key: 'random-imsi-number',
      });
      let sim_number: any = await Preferences.get({ key: 'sim-number' });
      this.apiService
        .getRequest(`${environment.LIVE_API_URL}riptec-contacts?imsi=${imsi_number.value}&phoneNumber=${sim_number.value}`)
        .subscribe(async (res: any) => {
          let contactsList = res.body.data.items;
          let riptecContacts : any = [];
          for await (let c of contactsList) {
            let cId = c.id;
            let cNumber = "+" + c.number;
            let cName = c.name;
            riptecContacts.push({
              contactId: cId,
              name: {
                display: cName,
                given: cName,
                middle: '',
                family: ' ',
              },
              phones: [
                {
                  type: 'custom',
                  label: 'mobile',
                  isPrimary: false,
                  number: cNumber,
                },
              ],
              phoneNumbers: [
                cNumber,
              ]
            });
          }

          this.riptec_contacts = riptecContacts;
          
          return resolve({
            status: true,
            contacts: this.riptec_contacts,
          });
        });
    });
  }

  async initilizeAllContacts() {
    if (Capacitor.isNativePlatform()) {
      return new Promise(async (resolve, reject) => {
        const projection = {
          name: true,
          phones: true,
          postalAddresses: true,
          organization: true,
          birthday: true,
          note: false, // iOS requires com.apple.developer.contacts.notes entitlement
          emails: true,
          urls: true,
          image: true,
        };

        const result = await Contacts.getContacts({
          projection,
        })
          .then(async (res) => {
            this.all_contacts = res.contacts;

            let filtered_contacts = [];
            for await (let contact of this.all_contacts) {
              if (contact.name !== undefined) {
                // iOS Contacts plugin returns empty strings (not undefined) for
                // missing givenName / familyName. Normalize both so downstream
                // sort & render logic always has a non-empty value.
                if (contact.name.family === undefined || contact.name.family === '') {
                  contact.name.family = ' ';
                }
                if (contact.name.given === undefined || contact.name.given === '') {
                  contact.name.given = ' ';
                }
                if (contact.phones !== undefined) {
                  filtered_contacts.push(contact);
                  let ExistingPhoneNumbers = [];
                  for (let phoneNumbers of contact.phones) {
                    ExistingPhoneNumbers.push(
                      phoneNumbers.number.replace(/-|\s|\(|\)/g, '').slice()
                    );
                  }
                  contact.phoneNumbers = [...new Set(ExistingPhoneNumbers)];
                }
              }
            }
            this.all_contacts = filtered_contacts;
            return resolve({
              status: true,
              contacts: filtered_contacts,
            });
          })
          .catch((err) => {
            console.log('getContacts failed:', err);
            return resolve({ status: false, contacts: [], error: String(err) });
          });
      });
    } else {
      return new Promise(async (resolve, reject) => {
        // Web/non-native: no device Contacts API available. Return an empty
        // device-contacts list (company/riptec contacts still load separately).
        this.all_contacts = [];
        return resolve({
          status: true,
          contacts: [],
        });
      });
    }
  }

  setSelectedContact(contact: any) {
    this.selectedContact = contact;
  }

  getSelectedContact() {
    return this.selectedContact;
  }

  async initilizeFavoriteContacts() {
    let favourites: any = await Preferences.get({ key: 'favorites' });
    if (favourites.value) {
      const { value } = await Preferences.get({ key: 'favorites' });
      this.all_favorite_contacts = JSON.parse(value);
    } else {
      this.all_favorite_contacts = [];
    }
  }

  async initilizeGroupsList() {
    let groups_list: any = await Preferences.get({ key: 'groups-list' });
    if (groups_list.value) {
      const { value } = await Preferences.get({ key: 'groups-list' });
      this.groups_list = JSON.parse(value);
    }
  }

  async initilizeSpeedDialList() {
    let speed_dial_list: any = await Preferences.get({
      key: 'speed-dial-list',
    });
    if (speed_dial_list.value) {
      const { value } = await Preferences.get({ key: 'speed-dial-list' });
      this.speed_dial_list = JSON.parse(value);
    }
  }

  async initilizeBypassList() {
    let bypass_list: any = await Preferences.get({ key: 'bypass-list' });
    if (bypass_list.value) {
      const { value } = await Preferences.get({ key: 'bypass-list' });
      this.bypass_list = JSON.parse(value);
    }
  }

  async initilizeProfiles() {
    let profiles: any = await Preferences.get({ key: 'profiles' });
    if (profiles.value) {
      const { value } = await Preferences.get({ key: 'profiles' });
      this.profiles = JSON.parse(value);
    } else {
      let imsi_number: any = await Preferences.get({
        key: 'random-imsi-number',
      });
      let sim_number: any = await Preferences.get({ key: 'sim-number' });
      this.apiService
        .getRequest('profiles', {
          offset: 0,
          limit: 20,
          imsi: imsi_number.value ? imsi_number.value : 0,
          sim_number: sim_number.value ? sim_number.value : 0,
        })
        .subscribe(async (res: any) => {
          this.profiles = res.body.data.profiles;

          const success = await this.customAppBgService.setBackgroundImageUrl(res.body.data.customAppBgUrl);
          if (success) {
            console.log('Custom background URL set successfully');
          } else {
            console.log('Failed to set custom background URL');
          }

          for await (let item of this.profiles) {
            let result: any = findPhoneNumbersInText('+' + item.number);
            if (result[0]?.number?.country === 'GB') {
              item.country = 'UK';
            } else {
              item.country = result[0]?.number?.country;
            }
            if (item.default) {
              /*
              await Preferences.set({
                key: 'default-profile',
                value: JSON.stringify(item),
              });
              */
            }
          }
          await Preferences.set({
            key: 'profiles',
            value: JSON.stringify(this.profiles),
          });
        });
    }
  }

  getCountryIsoFromPhoneNumber(number: any) {
    let result: any = findPhoneNumbersInText('+' + number);
    if (result[0].number.country === 'GB') {
      return 'UK';
    } else {
      return result[0].number.country;
    }
  }

  /**
   * Shows a toast with a RESET button when a 500 error occurs.
   * Clicking RESET will perform a full app reset and navigate to initial-setup.
   */
  async showResetToastOnError() {
    const toast = await this.toastController.create({
      message: 'Server error occurred. We recommend resetting the app.',
      duration: 10000,
      position: 'bottom',
      cssClass: 'reset-error-toast',
      buttons: [
        {
          text: 'RESET',
          handler: () => {
            this.performAppReset();
          }
        },
        {
          text: 'Dismiss',
          role: 'cancel'
        }
      ]
    });
    await toast.present();
  }

  /**
   * Performs a full app reset - clears all preferences and navigates to initial setup.
   */
  async performAppReset() {
    await Preferences.remove({ key: 'random-imsi-number' });
    await Preferences.remove({ key: 'sim-number' });
    await Preferences.remove({ key: 'initial-setup-visit-done-new' });
    await Preferences.remove({ key: 'favorites' });
    await Preferences.remove({ key: 'conxhub-call-dial-screen' });
    await Preferences.remove({ key: 'conxhub-defaults' });
    await Preferences.remove({ key: 'profiles' });
    this.router.navigate(['/initial-setup']);
  }
}
